/**
 * Sidebar collapse-state hook: the collapsed flag, the three logo-swap flags,
 * the collapse-transition timer ref, the lock/clear callbacks, and the unmount
 * timer-cleanup effect.
 *
 * `sidebarRef` is threaded in because `lockLogoSwapDuringCollapse` probes
 * `sidebarRef.current?.matches(':hover')` to decide the post-collapse hover
 * behavior; the ref itself stays owned by the orchestrator's JSX.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';

interface UseCollapseStateArgs {
  sidebarRef: RefObject<HTMLDivElement | null>;
}

export function useCollapseState({ sidebarRef }: UseCollapseStateArgs) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isCollapseLogoSwapLocked, setIsCollapseLogoSwapLocked] = useState(false);
  const [isCollapsedHoverSwapReady, setIsCollapsedHoverSwapReady] = useState(false);
  const [isCollapsedHoverResetPending, setIsCollapsedHoverResetPending] = useState(false);
  const collapseLogoSwapTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCollapseLogoSwapLock = useCallback(() => {
    if (collapseLogoSwapTimerRef.current) {
      clearTimeout(collapseLogoSwapTimerRef.current);
      collapseLogoSwapTimerRef.current = null;
    }
    setIsCollapseLogoSwapLocked(false);
    setIsCollapsedHoverSwapReady(false);
    setIsCollapsedHoverResetPending(false);
  }, []);

  const lockLogoSwapDuringCollapse = useCallback(() => {
    setIsCollapseLogoSwapLocked(true);
    setIsCollapsedHoverSwapReady(false);
    setIsCollapsedHoverResetPending(false);
    if (collapseLogoSwapTimerRef.current) {
      clearTimeout(collapseLogoSwapTimerRef.current);
    }
    // Match sidebar width transition (220ms) with a small buffer.
    collapseLogoSwapTimerRef.current = setTimeout(() => {
      const isPointerInsideSidebar = sidebarRef.current?.matches(':hover') ?? false;
      setIsCollapseLogoSwapLocked(false);
      if (isPointerInsideSidebar) {
        // Avoid immediate hover swap if cursor stayed inside after collapse.
        setIsCollapsedHoverResetPending(true);
        setIsCollapsedHoverSwapReady(false);
      } else {
        setIsCollapsedHoverResetPending(false);
        setIsCollapsedHoverSwapReady(true);
      }
      collapseLogoSwapTimerRef.current = null;
    }, 280);
  }, []);

  useEffect(() => {
    return () => {
      if (collapseLogoSwapTimerRef.current) {
        clearTimeout(collapseLogoSwapTimerRef.current);
        collapseLogoSwapTimerRef.current = null;
      }
    };
  }, []);

  return {
    isCollapsed,
    setIsCollapsed,
    isCollapseLogoSwapLocked,
    isCollapsedHoverSwapReady,
    isCollapsedHoverResetPending,
    setIsCollapsedHoverSwapReady,
    setIsCollapsedHoverResetPending,
    clearCollapseLogoSwapLock,
    lockLogoSwapDuringCollapse,
  };
}
